import { Box, H3, HStack, Link, Separator, Text, VStack } from '@betterment/samba-react';

import { SupportCardShimmer } from './LoadingSupportCard';

import type { SupportContact } from '~/graphql/schema';

const getContactTrackName = (title: string, linkType: 'email' | 'call', hasName: boolean): string | undefined => {
  const locations: Record<string, Record<string, string>> = {
    'Your advisor': { email: 'AdvisorEmail' },
    'Your client success manager': { email: 'AccountManagerEmail', call: 'AccountManagerCall' },
    'Your third-party administrator': { email: 'TPAEmail' },
    'Plan support': { email: 'PlanSupportEmail', call: 'PlanSupportCall' },
    'Onboarding support': { email: hasName ? 'OnboardingAgentEmail' : 'GeneralOnboardingEmail' },
    'Employee support': { email: 'EmployeeSupportEmail' },
  };
  return locations[title]?.[linkType];
};

export type Props = {
  contacts?: Array<SupportContact>;
};

export const SupportContacts = ({ contacts }: Props) => (
  <VStack
    css={{
      gap: 'space.400',
    }}
    data-track-event="ElementViewed"
    data-track-name="GetSupportCard"
    data-track-flow="SupportLanding"
    data-track-module="GetSupport"
  >
    {!contacts ? (
      <SupportCardShimmer />
    ) : (
      contacts.map(contact => (
        <Box key={contact.title} aria-label={contact.title} role="group">
          <Separator />
          <HStack
            css={{
              gap: 'space.500',
              mt: 'space.400',
            }}
          >
            <Box
              css={{
                flex: 1,
              }}
            >
              <H3 color="text" variant="title100">
                {contact.title}
              </H3>
              <Text as="span" color="text.weak" variant="text50">
                {contact.description}
              </Text>
            </Box>
            <VStack
              css={{
                flex: 1,
                alignItems: 'flex-start',
                gap: 'space.50',
              }}
            >
              {contact.name && <Text as="p">{contact.name}</Text>}
              {contact.phoneNumber && (
                <Text as="p">
                  {contact.phoneNumber}
                  {contact.availableHours && (
                    <>
                      <br />
                      <Text as="span" variant="text50" css={{ marginTop: 0 }}>
                        {contact.availableHours}
                      </Text>
                    </>
                  )}
                </Text>
              )}
              {contact.calendlyUrl && (
                <Link
                  href={contact.calendlyUrl}
                  data-track-event="ElementClicked"
                  data-track-name={getContactTrackName(contact.title, 'call', !!contact.name)}
                  data-track-flow="SupportLanding"
                  data-track-module="GetSupport"
                >
                  Schedule a call
                </Link>
              )}
              {contact.email && (
                <Link
                  href={`mailto:${contact.email}`}
                  data-track-event="ElementClicked"
                  data-track-name={getContactTrackName(contact.title, 'email', !!contact.name)}
                  data-track-flow="SupportLanding"
                  data-track-module="GetSupport"
                >
                  {contact.email}
                </Link>
              )}
            </VStack>
          </HStack>
        </Box>
      ))
    )}
  </VStack>
);
